/**
 * Button/popup for grid column visibility state.
 */

import {Popover} from '@mui/material';
import {GridColDef, GridColumnVisibilityModel} from '@mui/x-data-grid-pro';
import SliderInput from '@wandb/weave/common/components/elements/SliderInput';
import {Checkbox} from '@wandb/weave/components';
import {StyledSliderInput} from '@wandb/weave/components/Form/StyledSliderInput';
import {clsx} from 'clsx';
import React, {useCallback, useMemo, useRef, useState} from 'react';

import {maybePluralize} from '../../../../../../core/util/string';
import {Button} from '../../../../../Button';
import {DraggableGrow, DraggableHandle} from '../../../../../DraggablePopups';
import {TextField} from '../../../../../Form/TextField';
import {Icon} from '../../../../../Icon';
import {Tailwind} from '../../../../../Tailwind';
import {Tooltip} from '../../../../../Tooltip';
import {ColumnInfo} from '../../types';
import {TraceCallSchema} from '../wfReactInterface/traceServerClientTypes';

const AUTO_COLLAPSE_THRESHOLD = 10;

type ManageColumnsButtonProps = {
  columnInfo: ColumnInfo;
  columnVisibilityModel: GridColumnVisibilityModel;
  setColumnVisibilityModel: (model: GridColumnVisibilityModel) => void;
  // Header depth control props
  headerDepth: number;
  onHeaderDepthChange: (depth: number) => void;
  maxColumns?: number;
  minHeaderDepth?: number;
  maxHeaderDepth?: number;
  hideLabel?: boolean;
};

type ColumnNode = {
  field: string;
  label: string;
  isGroup: boolean;
  children: ColumnNode[];
  col?: GridColDef<TraceCallSchema>;
  expanded?: boolean;
};

type ColumnItemProps = {
  node: ColumnNode;
  level: number;
  columnVisibilityModel: GridColumnVisibilityModel;
  isGroupVisible: (node: ColumnNode) => boolean | 'mixed';
  toggleColumnShow: (field: string) => void;
  toggleGroup: (groupField: string) => void;
  toggleGroupVisibility: (node: ColumnNode, newVisibility: boolean) => void;
  collapsedGroups: Set<string>;
  hasNestedGroups?: boolean;
};

const ColumnItem: React.FC<ColumnItemProps> = ({
  node,
  level,
  columnVisibilityModel,
  isGroupVisible,
  toggleColumnShow,
  toggleGroup,
  toggleGroupVisibility,
  collapsedGroups,
  hasNestedGroups = false,
}) => {
  if (node.isGroup) {
    const expanded = !collapsedGroups.has(node.field);
    const visibility = isGroupVisible(node);
    const checked = visibility === true;
    const indeterminate = visibility === 'mixed';
    const idSwitch = `toggle-vis_${node.field}`;

    return (
      <div key={node.field}>
        <div
          className={clsx('flex items-center py-2')}
          style={{marginLeft: level > 0 ? '7px' : '0'}}>
          <Button
            size="small"
            variant="ghost"
            className="mr-4"
            icon={expanded ? 'chevron-down' : 'chevron-next'}
            onClick={() => toggleGroup(node.field)}
            tooltip={expanded ? 'Collapse' : 'Expand'}
          />
          <Checkbox
            id={idSwitch}
            size="small"
            checked={indeterminate ? 'indeterminate' : checked}
            onCheckedChange={() => {
              toggleGroupVisibility(node, !checked);
            }}
          />
          <label htmlFor={idSwitch} className="ml-6 cursor-pointer font-medium">
            {node.label}
          </label>
        </div>
        {expanded && (
          <div
            className="border-l border-moon-250"
            style={{marginLeft: level === 0 ? '34px' : '42px'}}>
            {node.children.map(child => {
              // Check if there are any nested groups at this level
              const hasNestedGroups = node.children.some(
                sibling => sibling.isGroup
              );
              return (
                <ColumnItem
                  key={child.field}
                  node={child}
                  level={level + 1}
                  columnVisibilityModel={columnVisibilityModel}
                  isGroupVisible={isGroupVisible}
                  toggleColumnShow={toggleColumnShow}
                  toggleGroup={toggleGroup}
                  toggleGroupVisibility={toggleGroupVisibility}
                  collapsedGroups={collapsedGroups}
                  hasNestedGroups={hasNestedGroups}
                />
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Regular column (not a group)
  const idSwitch = `toggle-vis_${node.field}`;
  const checked = columnVisibilityModel[node.field] ?? true;
  const disabled = !(node.col?.hideable ?? true);

  return (
    <div key={node.field}>
      <div
        className={clsx('flex items-center py-2', disabled ? 'opacity-40' : '')}
        style={{
          marginLeft:
            level === 0
              ? '28px'
              : level === 1
                ? hasNestedGroups
                  ? '36px'
                  : '12px'
                : '12px',
        }}>
        <Checkbox
          id={idSwitch}
          size="small"
          checked={checked}
          onCheckedChange={() => {
            toggleColumnShow(node.field);
          }}
          disabled={disabled}
        />
        <label
          htmlFor={idSwitch}
          className={clsx('ml-6', disabled ? '' : 'cursor-pointer')}>
          {node.label}
        </label>
      </div>
    </div>
  );
};

export const ManageColumnsButton = ({
  columnInfo,
  columnVisibilityModel,
  setColumnVisibilityModel,
  headerDepth,
  onHeaderDepthChange,
  maxColumns,
  minHeaderDepth = 1,
  maxHeaderDepth = 8,
  hideLabel = false,
}: ManageColumnsButtonProps) => {
  const progress = useMemo(() => {
    return (
      ((headerDepth - minHeaderDepth) / (maxHeaderDepth - minHeaderDepth)) * 100
    );
  }, [headerDepth, minHeaderDepth, maxHeaderDepth]);

  const [search, setSearch] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set()
  );
  const lowerSearch = search.toLowerCase();

  // Helper function to build nested column structure
  const buildNestedStructure = useCallback(
    (cols: GridColDef<TraceCallSchema>[]): ColumnNode[] => {
      // First, collect all columns by their prefixes to determine grouping
      const firstLevelGroups: Map<string, GridColDef<TraceCallSchema>[]> =
        new Map();
      const specialFields: GridColDef<TraceCallSchema>[] = [];

      cols.forEach(col => {
        // Special handling for summary.weave fields - don't nest them
        if (
          col.field.startsWith('summary.weave.') ||
          col.field.startsWith('feedback.')
        ) {
          specialFields.push(col);
          return;
        }

        const parts = col.field.split('.');
        if (parts.length >= 1) {
          const firstLevel = parts[0];
          if (!firstLevelGroups.has(firstLevel)) {
            firstLevelGroups.set(firstLevel, []);
          }
          firstLevelGroups.get(firstLevel)!.push(col);
        }
      });

      const rootNodes: ColumnNode[] = [];

      // Add special fields first (summary.weave and feedback)
      specialFields.forEach(col => {
        rootNodes.push({
          field: col.field,
          label: col.headerName ?? col.field,
          isGroup: false,
          children: [],
          col,
        });
      });

      // Process first-level groups
      firstLevelGroups.forEach((columns, firstLevelKey) => {
        // If there's only one column with this prefix and it's exactly 1 level deep, add directly
        if (columns.length === 1 && columns[0].field.split('.').length === 1) {
          rootNodes.push({
            field: columns[0].field,
            label: columns[0].headerName ?? columns[0].field,
            isGroup: false,
            children: [],
            col: columns[0],
          });
          return;
        }

        // Check if we need a first-level group
        const needsFirstLevelGroup = columns.length > 1;

        if (!needsFirstLevelGroup) {
          // Single column with this prefix - add directly
          const col = columns[0];
          rootNodes.push({
            field: col.field,
            label: col.headerName ?? col.field,
            isGroup: false,
            children: [],
            col,
          });
        } else {
          // Multiple columns with this prefix - create first-level group
          // Use a unique field identifier to prevent key collisions
          const groupField = `group:${firstLevelKey}`;
          const firstLevelNode: ColumnNode = {
            field: groupField,
            label: firstLevelKey,
            isGroup: true,
            children: [],
            expanded: !collapsedGroups.has(groupField),
          };

          // Now organize second-level groups within this first-level group
          const secondLevelGroups: Map<string, GridColDef<TraceCallSchema>[]> =
            new Map();
          const directChildren: GridColDef<TraceCallSchema>[] = [];

          columns.forEach(col => {
            const parts = col.field.split('.');
            if (parts.length <= 2) {
              // Direct child of first level
              directChildren.push(col);
            } else {
              // Needs second-level grouping
              const secondLevelKey = parts.slice(0, 2).join('.');
              if (!secondLevelGroups.has(secondLevelKey)) {
                secondLevelGroups.set(secondLevelKey, []);
              }
              secondLevelGroups.get(secondLevelKey)!.push(col);
            }
          });

          // Add direct children first
          directChildren.forEach(col => {
            firstLevelNode.children.push({
              field: col.field,
              label: col.headerName ?? col.field,
              isGroup: false,
              children: [],
              col,
            });
          });

          // Add second-level groups
          secondLevelGroups.forEach((secondLevelCols, secondLevelKey) => {
            if (secondLevelCols.length === 1) {
              // Single item in second level - add directly
              const col = secondLevelCols[0];
              firstLevelNode.children.push({
                field: col.field,
                label: col.headerName ?? col.field,
                isGroup: false,
                children: [],
                col,
              });
            } else {
              // Multiple items - create second-level group
              // Use a unique field identifier to prevent key collisions
              const secondGroupField = `group:${firstLevelKey}.${secondLevelKey}`;
              const secondLevelNode: ColumnNode = {
                field: secondGroupField,
                label: secondLevelKey, // Use the full path for the label
                isGroup: true,
                children: [],
                expanded: !collapsedGroups.has(secondGroupField),
              };

              secondLevelCols.forEach(col => {
                secondLevelNode.children.push({
                  field: col.field,
                  label: col.headerName ?? col.field,
                  isGroup: false,
                  children: [],
                  col,
                });
              });

              firstLevelNode.children.push(secondLevelNode);
            }
          });

          rootNodes.push(firstLevelNode);
        }
      });

      return rootNodes;
    },
    [collapsedGroups]
  );

  // Filter columns based on search
  const filteredCols = useMemo(() => {
    if (!search) return columnInfo.cols;
    return columnInfo.cols.filter((col: GridColDef<TraceCallSchema>) => {
      const value = col.field;
      const label = col.headerName ?? value;
      return label.toLowerCase().includes(lowerSearch);
    });
  }, [columnInfo.cols, search, lowerSearch]);

  // Build nested structure from filtered columns
  const nestedColumns = useMemo(
    () => buildNestedStructure(filteredCols),
    [filteredCols, buildNestedStructure]
  );

  // Calculate number of toggleable columns
  const numToggleable = useMemo(() => {
    let count = 0;
    const countToggleable = (nodes: ColumnNode[]) => {
      nodes.forEach(node => {
        if (!node.isGroup && (node.col?.hideable ?? true)) {
          count++;
        }
        if (node.children.length > 0) {
          countToggleable(node.children);
        }
      });
    };
    countToggleable(nestedColumns);
    return count;
  }, [nestedColumns]);

  const buttonSuffix = search ? `(${numToggleable})` : 'all';

  const ref = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const onClick = () => {
    if (!anchorEl) {
      const groupsToCollapse = getGroupsWithAllChildrenHidden(
        nestedColumns,
        columnVisibilityModel
      );
      setCollapsedGroups(groupsToCollapse);
    }
    setAnchorEl(anchorEl ? null : ref.current);
    setSearch('');
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popper' : undefined;
  const numHidden = Object.values(columnVisibilityModel).filter(
    v => v === false
  ).length;

  const toggleColumnShow = useCallback(
    (key: string) => {
      const on = columnVisibilityModel[key] ?? true;
      const newModel = {
        ...columnVisibilityModel,
        [key]: !on,
      };
      setColumnVisibilityModel(newModel);
    },
    [columnVisibilityModel, setColumnVisibilityModel]
  );

  const toggleGroup = useCallback((groupField: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupField)) {
        next.delete(groupField);
      } else {
        next.add(groupField);
      }
      return next;
    });
  }, []);

  const toggleGroupVisibility = useCallback(
    (node: ColumnNode, newVisibility: boolean) => {
      const newModel = {...columnVisibilityModel};

      const setNodeVisibility = (n: ColumnNode) => {
        if (!n.isGroup && n.col && (n.col.hideable ?? true)) {
          newModel[n.field] = newVisibility;
        }
        n.children.forEach(setNodeVisibility);
      };

      setNodeVisibility(node);
      setColumnVisibilityModel(newModel);
    },
    [columnVisibilityModel, setColumnVisibilityModel]
  );

  const isGroupVisible = useCallback(
    (node: ColumnNode): boolean | 'mixed' => {
      if (!node.isGroup) {
        return columnVisibilityModel[node.field] ?? true;
      }

      let hasVisible = false;
      let hasHidden = false;

      const checkVisibility = (n: ColumnNode) => {
        if (!n.isGroup && n.col) {
          const isVisible = columnVisibilityModel[n.field] ?? true;
          const isHideable = n.col.hideable ?? true;
          if (isHideable) {
            if (isVisible) hasVisible = true;
            else hasHidden = true;
          }
        }
        n.children.forEach(checkVisibility);
      };

      checkVisibility(node);

      if (hasVisible && hasHidden) return 'mixed';
      if (hasVisible) return true;
      return false;
    },
    [columnVisibilityModel]
  );

  const onHideAll = () => {
    const newModel = {...columnVisibilityModel};

    const hideNodes = (nodes: ColumnNode[]) => {
      nodes.forEach(node => {
        if (!node.isGroup && node.col && (node.col.hideable ?? true)) {
          newModel[node.field] = false;
        }
        if (node.children.length > 0) {
          hideNodes(node.children);
        }
      });
    };

    hideNodes(nestedColumns);
    setColumnVisibilityModel(newModel);
  };

  const onShowAll = () => {
    const newModel = {...columnVisibilityModel};

    const showNodes = (nodes: ColumnNode[]) => {
      nodes.forEach(node => {
        if (!node.isGroup && node.col && (node.col.hideable ?? true)) {
          newModel[node.field] = true;
        }
        if (node.children.length > 0) {
          showNodes(node.children);
        }
      });
    };

    showNodes(nestedColumns);
    setColumnVisibilityModel(newModel);
  };

  const columnLimitReached =
    maxColumns != null && columnInfo.cols.length >= maxColumns;

  return (
    <>
      <span ref={ref}>
        <Button
          variant="secondary"
          icon="column"
          tooltip="Manage columns"
          className={`h-32 w-32 ${hideLabel ? 'md:h-32 md:w-32' : 'md:h-32 md:w-max'}`}
          onClick={onClick}>
          <span className={hideLabel ? 'hidden' : 'hidden md:flex'}>
            Columns
          </span>
        </Button>
      </span>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              overflow: 'visible',
            },
          },
        }}
        onClose={() => setAnchorEl(null)}
        TransitionComponent={DraggableGrow}>
        <Tailwind>
          <div className="min-w-[360px] p-12">
            <DraggableHandle>
              <div className="flex justify-between pb-8">
                <div className="flex items-center gap-4 font-semibold">
                  Manage columns
                  {columnLimitReached && (
                    <Tooltip
                      content={`${maxColumns} column limit reached. Some columns are not available.`}
                      side="top"
                      trigger={
                        <Icon
                          name="warning-alt"
                          className="text-moon-500 cursor-help w-16 h-16"
                        />
                      }
                    />
                  )}
                </div>
                <div className="ml-16 text-moon-500">
                  {maybePluralize(numHidden, 'hidden column', 's')}
                </div>
              </div>
            </DraggableHandle>
            <div className="mb-8">
              <div className="flex items-center gap-12">
                <div className="flex items-center gap-4 flex-shrink-0">
                  <span className="font-semibold text-sm text-moon-800">
                    Header depth
                  </span>
                  <Tooltip
                    content="Max depth of nested column headers displayed in the table. Deeper columns will be truncated."
                    side="top"
                    trigger={
                      <Icon
                        name="info"
                        className="text-moon-500 cursor-help w-16 h-16"
                      />
                    }
                  />
                </div>
                <div className="flex-1">
                  <StyledSliderInput className="w-full" progress={progress}>
                    <SliderInput
                      value={headerDepth}
                      onChange={onHeaderDepthChange}
                      min={minHeaderDepth}
                      max={maxHeaderDepth}
                      step={1}
                      hasInput
                      className="w-full"
                    />
                  </StyledSliderInput>
                </div>
              </div>
            </div>
            <div className="mb-8">
              <TextField
                placeholder="Filter columns"
                icon="search"
                autoFocus
                value={search}
                onChange={setSearch}
              />
            </div>
            <div className="max-h-[300px] overflow-auto">
              {nestedColumns.map(node => {
                // For root level, hasNestedGroups doesn't apply
                return (
                  <ColumnItem
                    key={node.field}
                    node={node}
                    level={0}
                    columnVisibilityModel={columnVisibilityModel}
                    isGroupVisible={isGroupVisible}
                    toggleColumnShow={toggleColumnShow}
                    toggleGroup={toggleGroup}
                    toggleGroupVisibility={toggleGroupVisibility}
                    collapsedGroups={collapsedGroups}
                    hasNestedGroups={false}
                  />
                );
              })}
            </div>
            <div className="mt-8 flex items-center">
              <Button
                size="small"
                variant="ghost"
                icon="hide-hidden"
                disabled={numToggleable === 0}
                onClick={onHideAll}>
                {`Hide ${buttonSuffix}`}
              </Button>
              <div className="flex-auto" />
              <Button
                size="small"
                variant="ghost"
                icon="show-visible"
                disabled={numToggleable === 0}
                onClick={onShowAll}>
                {`Show ${buttonSuffix}`}
              </Button>
            </div>
          </div>
        </Tailwind>
      </Popover>
    </>
  );
};

// Helper to find groups that should be auto-collapsed (all children hidden)
const getGroupsWithAllChildrenHidden = (
  nodes: ColumnNode[],
  columnVisibilityModel: GridColumnVisibilityModel
): Set<string> => {
  const groupsToCollapse = new Set<string>();

  const isNodeHidden = (node: ColumnNode): boolean => {
    if (!node.isGroup) {
      const visible = columnVisibilityModel[node.field] ?? true;
      return !visible;
    }
    if (node.children.length === 0) {
      return false;
    }
    return node.children.every(isNodeHidden);
  };

  const findCollapsibleGroups = (nodeList: ColumnNode[]) => {
    nodeList.forEach(node => {
      if (node.isGroup) {
        // Only auto-collapse if:
        // 1. Group has more than AUTO_COLLAPSE_THRESHOLD children
        // 2. All children are hidden
        if (
          node.children.length > AUTO_COLLAPSE_THRESHOLD &&
          node.children.every(isNodeHidden)
        ) {
          groupsToCollapse.add(node.field);
        }
        // Recursively check nested groups
        findCollapsibleGroups(node.children);
      }
    });
  };

  findCollapsibleGroups(nodes);
  return groupsToCollapse;
};
